import type RAPIER_API from '@dimforge/rapier3d-compat';
import type {
  Collider,
  DynamicRayCastVehicleController,
  EventQueue,
  RigidBody,
  World,
} from '@dimforge/rapier3d-compat';
import { RAPIER, initRapier } from '../physics/rapier';
import type { EnvironmentDef } from '../levels/types';
import type { CarSnapshot, CarSpec, InputFrame, SimEvent, V3 } from './types';
import { FIXED_DT, KMH } from './types';
import {
  applyGearChange,
  createDrivetrain,
  stepDrivetrain,
  validateGearChange,
  type DrivetrainState,
} from './drivetrain';
import {
  createManual,
  stepManual,
  type ManualOutput,
  type ManualState,
} from './manualDrivetrain';
import { isForwardGear } from './types';
import { createBrakes, stepBrakes, type BrakeState } from './brakes';

export interface SimWorldOptions {
  spec: CarSpec;
  environment: EnvironmentDef;
  /** Factor de agarre global (1 = seco, lluvia ~0.7, hielo ~0.35). */
  gripFactor?: number;
  spawn: { pos: [number, number]; headingDeg: number };
}

export interface ConePose {
  pos: V3;
  quat: { x: number; y: number; z: number; w: number };
}

const CONN_Y = -0.15; // anclaje de suspensión respecto al origen del chasis
const SUSP_REST = 0.22;
const STEER_MAX_LOW_RAD = 0.6; // ~34°
const STEER_MAX_HIGH_RAD = 0.12; // a alta velocidad
/** El parámetro de freno de Rapier se comporta como impulso por paso: N·dt. */
const BRAKE_N_TO_PARAM = FIXED_DT;

const DEG = Math.PI / 180;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function yawQuat(headingDeg: number): { x: number; y: number; z: number; w: number } {
  const h = (headingDeg * DEG) / 2;
  return { x: 0, y: Math.sin(h), z: 0, w: Math.cos(h) };
}

/**
 * Mundo de simulación determinista a paso fijo. Sin dependencia de Three/React:
 * se puede ejecutar en tests de Node exactamente igual que en el navegador.
 */
export class SimWorld {
  readonly spec: CarSpec;
  readonly gripFactor: number;

  private world: World;
  private chassis: RigidBody;
  private chassisColliderHandle: number;
  private vehicle: DynamicRayCastVehicleController;
  private eventQueue: EventQueue;
  private colliderTags = new Map<number, string>();
  private cones: RigidBody[] = [];
  private collisionCooldown = new Map<number, number>();

  private drivetrain: DrivetrainState = createDrivetrain();
  /** Estado del tren motriz manual (null en automáticos). */
  private manual: ManualState | null = null;
  private manualOut: ManualOutput | null = null;
  private brakes: BrakeState = createBrakes();

  tick = 0;
  private steerRad = 0;
  private accelLong = 0;
  private prevFwdSpeed = 0;
  private odoM = 0;
  private wheelSpin = 0;
  private absActive = false;
  private tcActive = false;
  private hillHold = false;
  private parkLock = false;
  private shifting = false;
  private brakeLightOn = false;
  private signalPeakSteer = 0;
  private lastInput: InputFrame | null = null;

  static async create(opts: SimWorldOptions): Promise<SimWorld> {
    await initRapier();
    return new SimWorld(opts);
  }

  /** Requiere initRapier() resuelto (usar SimWorld.create salvo en el motor). */
  constructor(opts: SimWorldOptions) {
    this.spec = opts.spec;
    this.gripFactor = opts.gripFactor ?? 1;
    if (opts.spec.manual) this.manual = createManual(opts.spec);
    this.world = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
    this.world.timestep = FIXED_DT;
    this.eventQueue = new RAPIER.EventQueue(true);

    this.buildEnvironment(opts.environment);

    // --- Chasis ---
    const d = this.spec.dims;
    const spawnY = Math.abs(CONN_Y) + SUSP_REST + d.wheelRadiusM + 0.02;
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(opts.spawn.pos[0], spawnY, opts.spawn.pos[1])
      .setRotation(yawQuat(opts.spawn.headingDeg))
      .setCanSleep(false)
      .setCcdEnabled(true);
    this.chassis = this.world.createRigidBody(bodyDesc);
    const half = {
      x: (d.widthM / 2) * 0.92,
      y: 0.5,
      z: (d.lengthM / 2) * 0.9,
    };
    const colDesc = RAPIER.ColliderDesc.cuboid(half.x, half.y, half.z)
      .setMass(this.spec.massKg)
      .setFriction(0.4)
      .setRestitution(0.05)
      .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    const chassisCollider = this.world.createCollider(colDesc, this.chassis);
    this.chassisColliderHandle = chassisCollider.handle;

    // --- Vehículo raycast ---
    this.vehicle = this.world.createVehicleController(this.chassis);
    this.vehicle.indexUpAxis = 1; // Y
    this.vehicle.setIndexForwardAxis = 2; // Z (setter con nombre peculiar en el binding)
    const halfTrack = d.trackM / 2;
    const halfBase = d.wheelbaseM / 2;
    const wheelPos: Array<[number, number]> = [
      [-halfTrack, halfBase], // 0 delantera izq
      [halfTrack, halfBase], // 1 delantera dcha
      [-halfTrack, -halfBase], // 2 trasera izq
      [halfTrack, -halfBase], // 3 trasera dcha
    ];
    const grip = this.spec.tireGrip * this.gripFactor;
    for (let i = 0; i < 4; i++) {
      const [wx, wz] = wheelPos[i]!;
      this.vehicle.addWheel(
        { x: wx, y: CONN_Y, z: wz },
        { x: 0, y: -1, z: 0 },
        { x: -1, y: 0, z: 0 },
        SUSP_REST,
        d.wheelRadiusM,
      );
      this.vehicle.setWheelSuspensionStiffness(i, i < 2 ? 32 : 29);
      this.vehicle.setWheelSuspensionCompression(i, 3.4);
      this.vehicle.setWheelSuspensionRelaxation(i, 5.2);
      this.vehicle.setWheelMaxSuspensionTravel(i, 0.28);
      this.vehicle.setWheelMaxSuspensionForce(i, this.spec.massKg * 9.81 * 0.7);
      this.vehicle.setWheelFrictionSlip(i, 10.5 * grip);
      this.vehicle.setWheelSideFrictionStiffness(i, (i < 2 ? 0.82 : 1.0) * grip);
    }
  }

  private buildEnvironment(env: EnvironmentDef): void {
    // Suelo
    const [gx, gz] = env.groundSize;
    const groundBody = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(0, -0.5, 0),
    );
    const ground = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(gx / 2, 0.5, gz / 2).setFriction(1.0),
      groundBody,
    );
    this.colliderTags.set(ground.handle, 'ground');

    for (const prop of env.props) {
      if (prop.type === 'box') {
        const body = this.world.createRigidBody(
          RAPIER.RigidBodyDesc.fixed()
            .setTranslation(prop.pos[0], prop.size[1] / 2, prop.pos[1])
            .setRotation(yawQuat(prop.rotYDeg ?? 0)),
        );
        const col = this.world.createCollider(
          RAPIER.ColliderDesc.cuboid(prop.size[0] / 2, prop.size[1] / 2, prop.size[2] / 2)
            .setFriction(0.6),
          body,
        );
        this.colliderTags.set(col.handle, prop.tag ?? 'wall');
      } else if (prop.type === 'ramp') {
        const yaw = yawQuat(prop.rotYDeg ?? 0);
        const p = (prop.pitchDeg * DEG) / 2;
        const pitch = { x: Math.sin(p), y: 0, z: 0, w: Math.cos(p) };
        // q = yaw ⨯ pitch (producto de cuaterniones con ejes Y y X puros)
        const q = {
          x: yaw.w * pitch.x,
          y: yaw.y * pitch.w,
          z: -yaw.y * pitch.x,
          w: yaw.w * pitch.w,
        };
        const body = this.world.createRigidBody(
          RAPIER.RigidBodyDesc.fixed()
            .setTranslation(prop.pos[0], 0, prop.pos[1])
            .setRotation(q),
        );
        const col = this.world.createCollider(
          RAPIER.ColliderDesc.cuboid(prop.size[0] / 2, prop.size[1] / 2, prop.size[2] / 2)
            .setFriction(1.0),
          body,
        );
        this.colliderTags.set(col.handle, 'ramp');
      } else if (prop.type === 'cone') {
        const body = this.world.createRigidBody(
          RAPIER.RigidBodyDesc.dynamic().setTranslation(prop.pos[0], 0.26, prop.pos[1]),
        );
        const col = this.world.createCollider(
          RAPIER.ColliderDesc.cylinder(0.25, 0.16)
            .setMass(0.55)
            .setFriction(0.7)
            .setRestitution(0.1),
          body,
        );
        this.colliderTags.set(col.handle, 'cone');
        this.cones.push(body);
      }
      // 'lane', 'zone' y 'sign' no tienen colisión: los interpreta el evaluador/render
    }
  }

  /** Avanza un tick fijo de simulación. Devuelve los eventos producidos. */
  step(input: InputFrame): SimEvent[] {
    const events: SimEvent[] = [];
    const dt = FIXED_DT;
    this.tick++;
    const spec = this.spec;

    // --- Selector de marchas con bloqueos lógicos (solo automático) ---
    const fwdSpeed = this.forwardSpeed();
    if (!this.manual && input.gear !== this.drivetrain.gear) {
      const deny = validateGearChange(this.drivetrain.gear, input.gear, fwdSpeed, input.brake);
      if (deny) {
        events.push({ type: 'gearDenied', requested: input.gear, reason: deny });
      } else {
        applyGearChange(this.drivetrain, input.gear);
        events.push({ type: 'gearChanged', gear: input.gear });
      }
    }
    const gear = this.manual ? this.manual.gear : this.drivetrain.gear;

    // --- Cargas por eje (transferencia de peso) y límites de agarre ---
    const m = spec.massKg;
    const g = 9.81;
    const mu = spec.tireGrip * this.gripFactor;
    const transfer = clamp(
      (m * this.accelLong * spec.centerOfGravityHeightM) / spec.dims.wheelbaseM,
      -m * g * 0.35,
      m * g * 0.35,
    );
    const loadFront = clamp(m * g * 0.5 - transfer, m * g * 0.15, m * g * 0.85);
    const loadRear = clamp(m * g * 0.5 + transfer, m * g * 0.15, m * g * 0.85);
    const capFront = mu * loadFront;
    const capRear = mu * loadRear;

    // --- Transmisión (automática o manual según el coche) ---
    const rearDriven = spec.manual?.drivenAxle === 'rear';
    const driveGripCap = (rearDriven ? capRear : capFront) * 0.95;
    let driveForceN: number;
    let engineBrakeN: number;
    if (this.manual) {
      const out = stepManual(this.manual, spec, {
        throttle: input.throttle,
        clutch: input.clutch,
        ignition: input.ignition,
        requestedGear: input.gear,
        speedMs: fwdSpeed,
        gripCapN: driveGripCap,
        dt,
      });
      this.manualOut = out;
      events.push(...out.events);
      driveForceN = out.driveForceN; // el freno motor ya viene con signo
      engineBrakeN = 0;
      this.tcActive = out.wheelspin && spec.assists.tc;
      this.shifting = this.manual.pendingGear !== null;
    } else {
      const dtOut = stepDrivetrain(this.drivetrain, spec, {
        throttle: input.throttle,
        brake: input.brake,
        speedMs: fwdSpeed,
        gripCapN: driveGripCap,
        dt,
      });
      driveForceN = dtOut.driveForceN;
      engineBrakeN = dtOut.engineBrakeN;
      this.tcActive = dtOut.tcActive;
      this.shifting = dtOut.shifting;
    }

    // --- Pendiente bajo el coche (para hill hold) ---
    const fwd = this.forwardVector();
    const slopeSigned = Math.asin(clamp(gear === 'R' ? -fwd.y : fwd.y, -1, 1));

    // --- Frenos ---
    const brakeOut = stepBrakes(this.brakes, spec, {
      brake: input.brake,
      throttle: input.throttle,
      handbrake: input.handbrake,
      gear,
      speedMs: fwdSpeed,
      gripCapAxleN: [capFront, capRear],
      slopeRad: slopeSigned,
      massKg: m,
      tick: this.tick,
      dt,
      absAvailable: spec.assists.abs,
      hillHoldAvailable: spec.assists.hillHold,
    });
    this.absActive = brakeOut.absActive;
    this.hillHold = brakeOut.hillHold;
    this.parkLock = brakeOut.parkLock;
    this.brakeLightOn = input.brake > 0.04 || (engineBrakeN > 900 && spec.regenBraking === true);

    // Manual aparcado en marcha con el motor parado: la compresión retiene
    let parkedInGearN = 0;
    if (
      this.manual &&
      !this.manual.engineOn &&
      this.manual.gear !== 'N' &&
      input.clutch < 0.3 &&
      Math.abs(fwdSpeed) < 0.6
    ) {
      parkedInGearN = 2600;
      this.parkLock = true;
    }

    // --- Dirección: límite sensible a velocidad + rate limit ---
    const speedAbs = Math.abs(fwdSpeed);
    const maxSteer =
      STEER_MAX_HIGH_RAD +
      (STEER_MAX_LOW_RAD - STEER_MAX_HIGH_RAD) * clamp(1 - speedAbs / 33, 0, 1);
    // steer input +1 = derecha; el ángulo positivo de Rapier gira a la izquierda → invertir
    const targetSteer = -input.steer * maxSteer;
    const steerRate = 2.2 - clamp(speedAbs / 40, 0, 1) * 1.3;
    this.steerRad += clamp(targetSteer - this.steerRad, -steerRate * dt, steerRate * dt);

    // --- Aplicar a las ruedas ---
    const drivePer = driveForceN / 2;
    const engineBrakePer = engineBrakeN / 2;
    const frontBrakePer =
      brakeOut.frontAxleN / 2 + (rearDriven ? 0 : engineBrakePer) + parkedInGearN / 4;
    const rearBrakePer =
      brakeOut.rearAxleN / 2 + (rearDriven ? engineBrakePer : 0) + parkedInGearN / 4;
    for (let i = 0; i < 4; i++) {
      const front = i < 2;
      const driven = rearDriven ? !front : front;
      this.vehicle.setWheelSteering(i, front ? this.steerRad : 0);
      this.vehicle.setWheelEngineForce(i, driven ? drivePer : 0);
      const brakeN = front ? frontBrakePer : rearBrakePer;
      this.vehicle.setWheelBrake(i, brakeN * BRAKE_N_TO_PARAM);
    }

    // --- Resistencias: aerodinámica + rodadura ---
    const vel = this.chassis.linvel();
    const vLen = Math.hypot(vel.x, vel.z);
    if (vLen > 0.15) {
      const drag = spec.dragCoef * vLen * vLen + spec.rollingResist * m * g;
      const s = (-drag * dt) / vLen;
      this.chassis.applyImpulse({ x: vel.x * s, y: 0, z: vel.z * s }, true);
    }

    // --- Paso físico ---
    const chassisHandle = this.chassisColliderHandle;
    this.vehicle.updateVehicle(dt, undefined, undefined, (c: Collider) => c.handle !== chassisHandle);
    this.world.step(this.eventQueue);

    // --- Eventos de colisión (solo los que implican al chasis) ---
    this.eventQueue.drainCollisionEvents((h1, h2, started) => {
      if (!started) return;
      const other = h1 === chassisHandle ? h2 : h2 === chassisHandle ? h1 : null;
      if (other === null) return;
      const tag = this.colliderTags.get(other) ?? 'prop';
      if (tag === 'ground' || tag === 'ramp') return;
      const until = this.collisionCooldown.get(other) ?? 0;
      if (this.tick < until) return;
      this.collisionCooldown.set(other, this.tick + 50);
      events.push({ type: 'collision', tag, speedKmh: Math.abs(fwdSpeed) * KMH });
    });

    // --- Cancelación automática de intermitente tras el giro ---
    const prev = this.lastInput;
    if (prev) {
      const active = input.signalLeft ? 'left' : input.signalRight ? 'right' : null;
      if (active) {
        const dirSign = active === 'left' ? -1 : 1;
        if (input.steer * dirSign > 0.3) this.signalPeakSteer = Math.abs(input.steer);
        if (this.signalPeakSteer > 0.3 && Math.abs(input.steer) < 0.06) {
          events.push({ type: 'signalCancelled', side: active });
          this.signalPeakSteer = 0;
        }
      } else {
        this.signalPeakSteer = 0;
      }
    }

    // --- Telemetría ---
    const newFwd = this.forwardSpeed();
    this.accelLong = (newFwd - this.prevFwdSpeed) / dt;
    this.prevFwdSpeed = newFwd;
    this.odoM += Math.abs(newFwd) * dt;
    this.wheelSpin += (newFwd / spec.dims.wheelRadiusM) * dt;
    this.lastInput = input;

    return events;
  }

  private forwardVector(): V3 {
    const q = this.chassis.rotation();
    // f = q * (0,0,1)
    return {
      x: 2 * (q.x * q.z + q.w * q.y),
      y: 2 * (q.y * q.z - q.w * q.x),
      z: 1 - 2 * (q.x * q.x + q.y * q.y),
    };
  }

  private forwardSpeed(): number {
    const v = this.chassis.linvel();
    const f = this.forwardVector();
    return v.x * f.x + v.y * f.y + v.z * f.z;
  }

  snapshot(input?: InputFrame): CarSnapshot {
    const pos = this.chassis.translation();
    const rot = this.chassis.rotation();
    const vel = this.chassis.linvel();
    const fwdSpeed = this.forwardSpeed();
    const fwd = this.forwardVector();
    const d = this.spec.dims;
    const inp = input ?? this.lastInput;
    const wheels = [];
    for (let i = 0; i < 4; i++) {
      const front = i < 2;
      wheels.push({
        x: (i % 2 === 0 ? -1 : 1) * (d.trackM / 2),
        z: (front ? 1 : -1) * (d.wheelbaseM / 2),
        suspensionM: this.vehicle.wheelSuspensionLength(i) ?? SUSP_REST,
        steerRad: front ? this.steerRad : 0,
        spinRad: this.wheelSpin,
        inContact: this.vehicle.wheelIsInContact(i),
      });
    }
    const man = this.manual;
    const mOut = this.manualOut;
    const manualSnap =
      man === null
        ? null
        : {
            engineOn: man.engineOn,
            cranking: mOut?.cranking ?? false,
            stallCount: man.stallCount,
            clutchPedal: inp?.clutch ?? 0,
            engagement: mOut?.engagement ?? man.engagement,
            slipRpm: mOut?.slipRpm ?? 0,
            atBite: mOut?.atBite ?? false,
            clutchHeat: man.clutchHeat,
            gearRequested: inp?.gear ?? man.gear,
            overRev: mOut?.overRev ?? false,
            lugging: mOut?.lugging ?? false,
            shiftUpHint: mOut?.shiftUpHint ?? false,
            shiftDownHint: mOut?.shiftDownHint ?? false,
          };
    return {
      tick: this.tick,
      pos: { x: pos.x, y: pos.y, z: pos.z },
      quat: { x: rot.x, y: rot.y, z: rot.z, w: rot.w },
      vel: { x: vel.x, y: vel.y, z: vel.z },
      speedMs: fwdSpeed,
      speedKmh: Math.abs(fwdSpeed) * KMH,
      gear: man ? man.gear : this.drivetrain.gear,
      ratioIndex: man
        ? isForwardGear(man.gear)
          ? man.gear - 1
          : 0
        : this.drivetrain.ratioIndex,
      rpm: man ? man.rpm : this.drivetrain.rpm,
      shifting: this.shifting,
      absActive: this.absActive,
      tcActive: this.tcActive,
      hillHold: this.hillHold,
      parkLock: this.parkLock,
      handbrake: inp?.handbrake ?? false,
      signalLeft: inp?.signalLeft ?? false,
      signalRight: inp?.signalRight ?? false,
      lights: inp?.lights ?? 0,
      seatbelt: inp?.seatbelt ?? false,
      horn: inp?.horn ?? false,
      glance: inp?.glance ?? 'none',
      throttle: inp?.throttle ?? 0,
      brake: inp?.brake ?? 0,
      steerRad: this.steerRad,
      accelLong: this.accelLong,
      slopeRad: Math.asin(clamp(fwd.y, -1, 1)),
      odoM: this.odoM,
      wheels,
      brakeLight: this.brakeLightOn,
      manual: manualSnap,
    };
  }

  conePoses(): ConePose[] {
    return this.cones.map((b) => {
      const p = b.translation();
      const q = b.rotation();
      return { pos: { x: p.x, y: p.y, z: p.z }, quat: { x: q.x, y: q.y, z: q.z, w: q.w } };
    });
  }

  dispose(): void {
    this.world.removeVehicleController(this.vehicle);
    this.world.free();
  }
}

export type { RAPIER_API };
